import { SummarySectionTag } from 'app/area/case-report/case-table-view/constants';
import { ColumnDataType, FavouriteDataColumn } from 'app/components/favourite-layout/favourite/common/favourite-interfaces';
import { DomainAttributes } from 'app/infrastructure/ui/types-uidefinition';
import * as factItemComponents from 'app/area/case/components/fact-box';
import { CaseTableViewMetadata } from 'app/area/case-report/case-table-view/types-store';
import memoize from 'lodash/memoize';
import { Thumbnail } from 'app/area/case/components';
import { DisplayBoolean, DisplayCodes } from 'ui';

const groupSummarySectionFieldsByTag = (fields) => {
  return fields
    .filter(item => item.summarySectionTag)
    .reduce((acc, item) => {
      const tag = item.summarySectionTag as string;
      if (!acc[tag]) {
        acc[tag] = { domain: [], entityTypePath: [] };
      }
      acc[tag].domain.push(item.domain?.v);
      acc[tag].entityTypePath = item.entityTypePath;
      return acc;
    }, {} as { [key: string]: { domain: number[] } });
};

export const mapColumnsToSummarySection = (fields): Partial<FavouriteDataColumn>[] => {
  if (!fields?.length) {
    return [];
  }
  const summarySectionGroupedByTags = groupSummarySectionFieldsByTag(fields);
  const includedTags = new Set<string>();
  const includedFields = fields.map((item, index) => {
    const summarySectionTag = item.summarySectionTag;
    const summarySectionGroup = summarySectionGroupedByTags[summarySectionTag];
    const summarySectionForActions = [SummarySectionTag.ActionDueStatus, SummarySectionTag.ActionStatus].includes(summarySectionTag); 
    if (summarySectionTag && summarySectionGroup && !includedTags.has(summarySectionTag)) {
      includedTags.add(summarySectionTag);
      return {
        summarySectionTag,
        id: index,
        dataType: ColumnDataType.SummarySection,
        columnName: summarySectionTag,
        shortText: summarySectionForActions ? item.shortText : item.summarySectionLabel || item.shortText,
        domain: summarySectionGroup.domain,
        entityTypePath: summarySectionGroup.entityTypePath || []  
      };
    }
    if (includedTags.has(summarySectionTag)) {
      return null;
    }
    return {...item, domain: item.domain?.v};
  }).filter(Boolean);

  return includedFields;
};

export const mapCaseTableViewDomains = (domains) => {
  if (!domains) {
    return {};
  }
  return domains.reduce((acc, domain) => {
    acc[domain.caseTypeId] = domain?.attributes || [];
    return acc;
  }, {});
};

export const getDomainAttributesForRow = memoize(({ domainAttributes, caseType }:
  { domainAttributes: CaseTableViewMetadata['domains']; caseType: number }): Record<number, DomainAttributes[]> | {} => {
  if (!domainAttributes?.[caseType]) {
    return {};
  }
  return domainAttributes[caseType];
});

export const summarySectionComponentMap = {
  [SummarySectionTag.Actions]: factItemComponents.ActionFactItem,
  [SummarySectionTag.Assessment]: factItemComponents.AssessmentFactItem,
  [SummarySectionTag.Attachments]: factItemComponents.AttachedFilesFactItem,
  [SummarySectionTag.Bowtie]: factItemComponents.BowtieFactItem,
  [SummarySectionTag.Causes]: factItemComponents.CausesFactItem,
  [SummarySectionTag.CheckList]: factItemComponents.ChecklistFactItem,
  [SummarySectionTag.Comments]: factItemComponents.CommentsFactItem,
  [SummarySectionTag.ConnectedCases]: factItemComponents.ConnectedCasesFactItem,
  [SummarySectionTag.Consequences]: factItemComponents.ConsequencesFactItem,
  [SummarySectionTag.IntegrityStatus]: factItemComponents.IntegrityStatusFactItem,
  [SummarySectionTag.IntegrityStatusBarrierEval]: factItemComponents.IntegrityStatusBarrierEvalFactItem,
  [SummarySectionTag.InvolvedDrug]: factItemComponents.InvolvedDrugFactItem,
  [SummarySectionTag.InvolvedPersons]: factItemComponents.InvolvedPersonFactItem,
  [SummarySectionTag.ResidualRisk]: factItemComponents.ResidualRiskFactItem,
  [SummarySectionTag.Risk]: factItemComponents.RiskFactItem,
  [SummarySectionTag.Image]: Thumbnail,
  [SummarySectionTag.Status]: DisplayCodes,
  [SummarySectionTag.CaseDueStatus]: DisplayCodes,
  [SummarySectionTag.Confidential]: DisplayBoolean,
  [SummarySectionTag.ActionStatus]: DisplayCodes,
  [SummarySectionTag.ActionDueStatus]: DisplayCodes,
  [SummarySectionTag.GasTest]: factItemComponents.GasTestFactItem
};
